import { useEffect, useRef, useState } from 'react'
import { useDataProvider } from 'react-admin'

// Datev's OAuth flow is uniquely two-step: round 1 mints a short-lived token
// we use to fetch the user's accessible Datev clients, so we can present them
// as a dropdown before kicking off round 2. This hook drives that fetch.
// Hardcoded to the `datev_oauth` connector — no other connector behaves this way.
const DATEV_CONNECTOR_NAME = 'datev_oauth'
export const PENDING_SECOND_OAUTH_STATUS = 'Pending second OAuth'

/**
 * Datev-only: once the tenant lands in "Pending second OAuth", fetch the
 * non-sensitive secrets (which contain the Datev clients list) so the
 * post-OAuth selection UI can be populated.
 *
 * Returns:
 *   - authStatus: the current `<CONNECTOR>_AUTH_STATUS` for the tenant
 *   - datevClients: array of Datev clients available to the user, or null
 *
 * Optional callbacks (latest-value via ref, so they don't need to be memoized):
 *   - onLoaded(clients): fired once per successful fetch
 *   - onError(error):    fired once per failed fetch
 */
export const useDatevClients = ({
    connector,
    tenant,
    enabled = true,
    onLoaded,
    onError,
}) => {
    const dataProvider = useDataProvider()
    const [datevClients, setDatevClients] = useState(null)

    const onLoadedRef = useRef(onLoaded)
    const onErrorRef = useRef(onError)
    useEffect(() => {
        onLoadedRef.current = onLoaded
        onErrorRef.current = onError
    })

    const isDatev = connector?.name === DATEV_CONNECTOR_NAME
    const tenantId = tenant?.id
    const authStatus =
        isDatev && tenant
            ? tenant?.status?.auth?.[
                  `${DATEV_CONNECTOR_NAME.toUpperCase()}_AUTH_STATUS`
              ]
            : null

    useEffect(() => {
        if (!enabled || !isDatev || !tenantId) return

        if (authStatus !== PENDING_SECOND_OAUTH_STATUS) {
            setDatevClients((prev) => (prev ? null : prev))
            return
        }

        let cancelled = false
        dataProvider
            .V1_ACTION('tenants', {
                id: tenantId,
                action: `connectors/${DATEV_CONNECTOR_NAME}/sanitized_secrets`,
                method: 'GET',
            })
            .then((res) => {
                if (cancelled) return
                const clients = res.data?.clients || []
                setDatevClients(clients)
                onLoadedRef.current?.(clients)
            })
            .catch((err) => {
                if (cancelled) return
                console.debug(err)
                onErrorRef.current?.(err)
            })

        return () => {
            cancelled = true
        }
    }, [enabled, isDatev, authStatus, tenantId, dataProvider])

    return { authStatus, datevClients }
}
